import React, { useCallback, useEffect, useReducer } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import {
  broadcastSessionSignal,
  clearActivity,
  fetchSessionStatus,
  getLegacyReportToken,
  logout,
  onSessionLost,
  onSessionSignal,
  setAuthenticated,
  switchCompany,
} from '../helpers/session';
import { GET_LOGGED_IN_USER_QUERY } from '../constants/queries/dashboard';
import GlobalContext from './GlobalContext';
import { useDebouncedEffect } from '../hooks';
import commonReducer from '../common/commonReducer';


function useGlobalContextProvider() {

  const client = useApolloClient();

  const [{
    selectedCompany,
    selectedCompanyId,
    loggedInUser,
    showGlobalSearchBar,
    showGlobalCustomReportModal,
    sessionStatus
  }, setState] = useReducer(commonReducer, {
    selectedCompany: undefined,
    selectedCompanyId: undefined,
    loggedInUser: undefined,
    showGlobalSearchBar: false,
    showGlobalCustomReportModal: false,
    sessionStatus: 'unknown'
  });

  const [getLoggedInUserQuery, {
    data: userData,
    error,
    loading: loggedInUserLoading
  }] = useLazyQuery(GET_LOGGED_IN_USER_QUERY, { fetchPolicy: 'no-cache' });

  const loadLoggedInUser = useCallback(async () => {
    try {
      getLoggedInUserQuery({
        variables: {}
      }).then(result=>{
        if (!result?.data?.me){
          clearSession();
        }
      }).catch(err => {
        clearSession();
      });
    } catch (err) {
      clearSession();
    }
    // eslint-disable-next-line
  }, [getLoggedInUserQuery]);


  const clearSession = useCallback(() => {
    clearActivity();
    setAuthenticated(false);
    setState({
      loggedInUser: undefined,
      sessionStatus: 'anonymous'
    });
    // eslint-disable-next-line no-unused-expressions
    sessionStorage && sessionStorage.clear();
  }, []);

  const setSessionAuthenticated = useCallback(() => {
    setAuthenticated(true);
    setState({ sessionStatus: 'authenticated' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (getLegacyReportToken()) {
      setState({ sessionStatus: 'authenticated' });
    } else {
      fetchSessionStatus().then((status) => {
        if (cancelled) return;
        setState({ sessionStatus: status?.authenticated ? 'authenticated' : 'anonymous' });
      });
    }
    const offSignal = onSessionSignal((signal) => {
      if (signal === 'logged-out') {
        client.clearStore().catch(() => {});
        clearSession();
      } else if (signal === 'logged-in') {
        fetchSessionStatus().then((status) => {
          if (!cancelled && status?.authenticated) {
            setState({ sessionStatus: 'authenticated' });
          }
        });
      }
    });
    const offLost = onSessionLost(() => {
      client.clearStore().catch(() => {});
      clearSession();
    });
    return () => {
      cancelled = true;
      offSignal();
      offLost();
    };
  }, [client, clearSession]);

  const logoutUser = useCallback(async () => {
    try {
      const result = await logout();
      if (result?.success !== true) {
        console.log('Logout was not successful', result);
      }
    } catch (err) {
      console.log(err);
    }
    broadcastSessionSignal('logged-out');
    await client.clearStore().catch(() => {});
    clearSession();
  }, [client, clearSession]);


  useDebouncedEffect(() => {
    if (userData) {
      if (userData.me) {
        setState({
          loggedInUser: { ...userData.me }
        });
        return () => {
          setState({
            loggedInUser: undefined
          });
        };
      } else {
        clearSession();
      }
    }
    // eslint-disable-next-line
  }, 100, [userData]);


  useEffect(() => {
    if (error) {
      if (error.message.includes('UNAUTHORIZED')) {
        clearSession();
      }
    }
  }, [error, clearSession]);

  const selectActiveSessionCompany = useCallback(async (selectedCompanyId) => {
    if (selectedCompanyId) {
      await client.clearStore();
      const data = await switchCompany(selectedCompanyId);
      if (data?.success && data.user) {
        setState({
          loggedInUser: { ...data.user }
        });
        return true;
      }
    }
    return false;
  }, [client]);


  const setShowGlobalSearchBar = useCallback((val) => {
    setState({
      showGlobalSearchBar: val
    });
  }, []);

  const setShowGlobalCustomReportModal = useCallback((val) => {
    setState({
      showGlobalCustomReportModal: val
    });
  }, []);


  return {
    selectedCompany,
    selectedCompanyId,
    loggedInUser,
    loadLoggedInUser,
    showGlobalSearchBar,
    showGlobalCustomReportModal,
    setShowGlobalSearchBar,
    setShowGlobalCustomReportModal,
    clearSession,
    setState,
    selectActiveSessionCompany,
    logoutUser,
    loggedInUserLoading,
    getLoggedInUserQuery,
    sessionStatus,
    setSessionAuthenticated
  };

}

const GlobalContextProvider = React.memo(({ children }) => {

  const {
    selectedCompany,
    selectedCompanyId,
    loggedInUser,
    loadLoggedInUser,
    setShowGlobalSearchBar,
    showGlobalSearchBar,
    clearSession,
    setState,
    showGlobalCustomReportModal,
    setShowGlobalCustomReportModal,
    selectActiveSessionCompany,
    logoutUser,
    getLoggedInUserQuery,
    loggedInUserLoading,
    sessionStatus,
    setSessionAuthenticated
  } = useGlobalContextProvider({});

  return (<GlobalContext.Provider value={{
    selectedCompany,
    selectedCompanyId,
    loggedInUser,
    setGlobalState: setState,
    clearSession,
    showGlobalSearchBar,
    logoutUser,
    setShowGlobalSearchBar,
    loadLoggedInUser,
    showGlobalCustomReportModal,
    setShowGlobalCustomReportModal,
    selectActiveSessionCompany,
    loggedInUserLoading,
    getLoggedInUserQuery,
    sessionStatus,
    setSessionAuthenticated
  }}>
    {children}
  </GlobalContext.Provider>);
});


export default GlobalContextProvider;
